import { useState, useEffect,useCallback } from "react";
import API_CONFIG from "../../../Configuration/apiConfig";
import apiClient from "../../../Configuration/apiClient";

const getMonthRangeBody = (fromDate, toDate) => {
  const [fromYear, fromMonthNum, fromDay] = fromDate.split("-").map(Number);
  const [toYear, toMonthNum, toDay] = toDate.split("-").map(Number);
  return {
    from: { date: fromDay, month: fromMonthNum, year: fromYear },
    to: { date: toDay, month: toMonthNum, year: toYear },
  };
};

export function useFilters(fromDate, toDate) {
  const [regionList, setRegionList] = useState([]);
  const [asmList, setAsmList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [channelList, setChannelList] = useState([]);
  const [typeList, setTypeList] = useState([]);

  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedAsm, setSelectedAsm] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingSubRegion, setLoadingSubRegion] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);
  const [loadingChannel, setLoadingChannel] = useState(false);
  const [loadingType, setLoadingType] = useState(false);
  const [error, setError] = useState("");

  // Fetch regions when dates change
  useEffect(() => {
    if (!fromDate || !toDate) return;
    const fetchRegions = async () => {
      setLoadingRegions(true);
      setError("");
      try {
        const requestBody = getMonthRangeBody(fromDate, toDate);
        const userId =
          localStorage.getItem("userId") || sessionStorage.getItem("userId");
        const res = await apiClient.request({
          method: "POST",
          url: `${API_CONFIG.ENDPOINTS.ASM_REPORT.REGIONDP}`,
          data: requestBody,
          params: { userId },
        });
        const regions = Array.isArray(res.data) ? res.data : [res.data];
        setRegionList(regions);
        setSelectedRegions([]);
        setSelectedAsm([]);
        setAsmList([]);
        setSelectedDistrict([]);
        setDistrictList([]);
        setSelectedChannels([]);
        setChannelList([]);
        setSelectedTypes([]);
        setTypeList([]);
      } catch (err) {
        console.error("Failed to load regions:", err);
        setError("Failed to load region dropdown data.");
        setRegionList([]);
      } finally {
        setLoadingRegions(false);
      }
    };
    fetchRegions();
  }, [fromDate, toDate]);

  // Fetch ASMs when regions change
  useEffect(() => {
    if (selectedRegions.length === 0 || !fromDate || !toDate) {
      setAsmList([]);
      setSelectedAsm([]);
      return;
    }
    const fetchAsms = async () => {
      setLoadingSubRegion(true);
      setError("");
      try {
        const requestBody = {
          filters: {
            dropdown1: selectedRegions.map((r) => r.value).join(", "),
          },
          timePeriod: getMonthRangeBody(fromDate, toDate),
          userId:
            localStorage.getItem("userId") || sessionStorage.getItem("userId"),
        };
        const res = await apiClient.post(
          `${API_CONFIG.ENDPOINTS.ASM_REPORT.ASM}?roleType=ASM`,
          requestBody
        );
        const asms = Array.isArray(res.data) ? res.data : [res.data];
        setAsmList(asms);
        setSelectedAsm([]);
      } catch (err) {
        console.error("Failed to load ASMs:", err);
        setError("Failed to load ASM dropdown data.");
        setAsmList([]);
      } finally {
        setLoadingSubRegion(false);
      }
    };
    fetchAsms();
  }, [selectedRegions, fromDate, toDate]);

  // Fetch cities when ASM changes
  useEffect(() => {
    if (selectedAsm.length === 0) {
      setDistrictList([]);
      setSelectedDistrict([]);
      return;
    }
    const fetchCities = async () => {
      setLoadingCity(true);
      setError("");
      try {
        let url = `${API_CONFIG.ENDPOINTS.STORE_REPORT.CITYDP}`;
        const queryParams = selectedAsm
          .map((asm) => `asmNames=${encodeURIComponent(asm.value)}`)
          .join("&");
        url += `?${queryParams}`;
        const requestBody = getMonthRangeBody(fromDate, toDate);
        const res = await apiClient.post(url, requestBody);
        const cities = Array.isArray(res.data) ? res.data : [res.data];
        setDistrictList(cities);
        setSelectedDistrict([]);
      } catch (err) {
        console.error("Failed to load cities:", err);
        setError("Failed to load city dropdown data.");
        setDistrictList([]);
      } finally {
        setLoadingCity(false);
      }
    };
    fetchCities();
  }, [selectedAsm, fromDate, toDate]);

  // Fetch channels when district changes
  useEffect(() => {
    if (selectedDistrict.length === 0 || !fromDate || !toDate) {
      setChannelList([]);
      setSelectedChannels([]);
      setTypeList([]);
      setSelectedTypes([]);
      return;
    }
    const fetchChannels = async () => {
      setLoadingChannel(true);
      setTypeList([]);
      setSelectedTypes([]);
      setError("");
      try {
        const requestBody = getMonthRangeBody(fromDate, toDate);
        const promises = selectedDistrict.map((city) =>
          apiClient.request({
            method: "POST",
            url: `${API_CONFIG.ENDPOINTS.STORE_REPORT.CHANNELDP}?cities=${encodeURIComponent(city.value)}`,
            data: requestBody,
          })
        );
        const results = await Promise.all(promises);
        const allChannels = results.flatMap((res) =>
          Array.isArray(res.data) ? res.data : [res.data]
        );
        setChannelList([...new Set(allChannels)]);
        setSelectedChannels([]);
      } catch (err) {
        console.error("Failed to load channels:", err);
        setError("Failed to load channel dropdown data.");
        setChannelList([]);
      } finally {
        setLoadingChannel(false);
      }
    };
    fetchChannels();
  }, [selectedDistrict, fromDate, toDate]);

  // Fetch types when channel changes
  useEffect(() => {
    if (selectedChannels.length === 0) {
      setTypeList([]);
      setSelectedTypes([]);
      return;
    }
    if (!fromDate || !toDate) return;
    const fetchType = async () => {
      setLoadingType(true);
      try {
        const requestBody = getMonthRangeBody(fromDate, toDate);
        const promises = selectedChannels.map((channel) =>
          apiClient.request({
            method: "POST",
            url: `${API_CONFIG.ENDPOINTS.ASM_REPORT.TYPEDP}?channels=${encodeURIComponent(channel.value)}`,
            data: requestBody,
          })
        );
        const results = await Promise.all(promises);
        const allTypes = results.flatMap((res) =>
          Array.isArray(res.data) ? res.data : [res.data]
        );
        setTypeList([...new Set(allTypes)]);
        setSelectedTypes([]);
      } catch (err) {
        console.error("Failed to load types:", err);
        setError("Failed to load type dropdown data.");
        setTypeList([]);
      } finally {
        setLoadingType(false);
      }
    };
    fetchType();
  }, [selectedChannels, fromDate, toDate]);

  // Build select options
  const regionOptions = regionList.map((item) => ({ value: item, label: item }));
  const asmOptions = asmList.map((item) => ({ value: item, label: item }));
  const cityOptions = districtList.map((item) => ({ value: item, label: item }));
  const channelOptions = channelList.map((item) => ({ value: item, label: item }));
  const typeOptions = typeList.map((item) => ({ value: item, label: item }));

  // Build filter body for main data fetch
 const getFilterBody = useCallback(() => {
  if (selectedRegions.length === 0) return {};
  return {
    dropdown1: selectedRegions.map((r) => r.value).join(", "),
    dropdown2: selectedAsm.map((sr) => sr.value).join(", "),
    dropdown3: selectedDistrict.map((c) => c.value).join(", "),
    dropdown4: selectedChannels.map((ch) => ch.value).join(", "),
    dropdown5: selectedTypes.map((t) => t.value).join(", "),
  };
}, [selectedRegions, selectedAsm, selectedDistrict, selectedChannels, selectedTypes]);

  return {
    // Options
    regionOptions, asmOptions, cityOptions, channelOptions, typeOptions,
    // Selections
    selectedRegions, setSelectedRegions,
    selectedAsm, setSelectedAsm,
    selectedDistrict, setSelectedDistrict,
    selectedChannels, setSelectedChannels,
    selectedTypes, setSelectedTypes,
    // Loading states
    loadingRegions, loadingSubRegion, loadingCity, loadingChannel, loadingType,
    // Error
    error,
    // Helper
    getFilterBody,
  };
}
